import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useChatContext } from '../../context/ChatContext';
import { toast } from 'react-toastify';
import { FiSend, FiPlus, FiSearch, FiUsers, FiUser, FiX, FiMessageCircle, FiPaperclip, FiFile, FiDownload } from 'react-icons/fi';

const ChatPage = () => {
  const { user } = useAuth();
  const { socket, setActiveRoomId, markRoomRead } = useChatContext();
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [showUserList, setShowUserList] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupMembers, setGroupMembers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  const [roomSearch, setRoomSearch] = useState('');
  const typingTimeout = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const FILE_BASE = 'https://hrms.teleforce.cx/backend/api';

  // Clear active room when leaving the page so notifications resume
  useEffect(() => () => setActiveRoomId(null), [setActiveRoomId]);


  // Socket message events
  useEffect(() => {
    if (!socket) return;

    socket.on('new_message', (msg) => {
      setMessages(prev => {
        if (prev.find(m => m._id === msg._id)) return prev;
        return [...prev, msg];
      });
      // Update room last message & unread
      setRooms(prev => prev.map(r => {
        if (r._id !== msg.roomId) {
          // Increment unread if not active room
          if (activeRoom?._id !== msg.roomId && msg.sender._id !== user._id) {
            return { ...r, unreadCount: (r.unreadCount || 0) + 1 };
          }
          return r;
        }
        return { ...r, lastMessage: msg, lastActivity: msg.createdAt };
      }).sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity)));
    });

    socket.on('user_typing', ({ name, isTyping }) => {
      setTypingUsers(prev =>
        isTyping ? [...new Set([...prev, name])] : prev.filter(n => n !== name)
      );
    });

    return () => {
      socket.off('new_message');
      socket.off('user_typing');
    };
  }, [socket, activeRoom, user._id]);

  const fetchRooms = useCallback(async () => {
    try {
      const res = await api.get('/chat/rooms');
      setRooms(res.data);
    } catch {
      toast.error('Failed to load chats');
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/chat/users');
      setAllUsers(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchRooms();
    fetchUsers();
  }, [fetchRooms, fetchUsers]);

  const openRoom = async (room) => {
    setActiveRoom(room);
    setActiveRoomId(room._id);
    setMessages([]);
    setTypingUsers([]);
    if (socket) socket.emit('join_room', room._id);

    try {
      const res = await api.get(`/chat/rooms/${room._id}/messages`);
      setMessages(res.data);
      // Mark read
      const prevUnread = room.unreadCount || 0;
      await api.put(`/chat/rooms/${room._id}/read`);
      if (socket) socket.emit('mark_read', { roomId: room._id });
      setRooms(prev => prev.map(r => r._id === room._id ? { ...r, unreadCount: 0 } : r));
      if (prevUnread > 0) markRoomRead(prevUnread);
    } catch {
      toast.error('Failed to load messages');
    }
  };

  const sendMessage = (e) => {
    e.preventDefault();
    if (!input.trim() || !activeRoom || !socket) return;
    socket.emit('send_message', { roomId: activeRoom._id, content: input.trim() });
    setInput('');
    clearTimeout(typingTimeout.current);
    socket.emit('typing', { roomId: activeRoom._id, isTyping: false });
  };

  const handleTyping = (e) => {
    setInput(e.target.value);
    if (!socket || !activeRoom) return;
    socket.emit('typing', { roomId: activeRoom._id, isTyping: true });
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      socket.emit('typing', { roomId: activeRoom._id, isTyping: false });
    }, 1500);
  };

  const sendFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeRoom) return;
    e.target.value = '';
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/chat/rooms/${activeRoom._id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch {
      toast.error('File upload failed');
    }
  };

  const renderFileMessage = (msg) => {
    const url = `${FILE_BASE}${msg.fileUrl}`;
    const isImage = msg.fileType?.startsWith('image/');
    if (isImage) {
      return (
        <a href={url} target="_blank" rel="noreferrer">
          <img src={url} alt={msg.fileName} style={{ maxWidth: 220, maxHeight: 180, borderRadius: 8, display: 'block' }} />
        </a>
      );
    }
    const kb = msg.fileSize ? `${(msg.fileSize / 1024).toFixed(1)} KB` : '';
    return (
      <a href={url} download={msg.fileName} className="chat-file-attachment">
        <FiFile size={20} />
        <div>
          <div className="chat-file-name">{msg.fileName}</div>
          {kb && <div className="chat-file-size">{kb}</div>}
        </div>
        <FiDownload size={16} style={{ marginLeft: 'auto' }} />
      </a>
    );
  };

  const startDirectChat = async (targetUser) => {
    try {
      const res = await api.post('/chat/rooms', { type: 'direct', targetUserId: targetUser._id });
      const room = res.data;
      setShowUserList(false);
      setUserSearch('');
      setRooms(prev => {
        const exists = prev.find(r => r._id === room._id);
        return exists ? prev : [{ ...room, unreadCount: 0 }, ...prev];
      });
      openRoom(room);
    } catch {
      toast.error('Failed to open chat');
    }
  };

  const createGroup = async () => {
    if (!groupName.trim()) { toast.error('Group name required'); return; }
    if (groupMembers.length === 0) { toast.error('Add at least one member'); return; }
    try {
      const res = await api.post('/chat/rooms', {
        type: 'group',
        name: groupName.trim(),
        memberIds: groupMembers,
      });
      const room = res.data;
      setShowGroupModal(false);
      setGroupName('');
      setGroupMembers([]);
      setRooms(prev => [{ ...room, unreadCount: 0 }, ...prev]);
      openRoom(room);
    } catch {
      toast.error('Failed to create group');
    }
  };

  // Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getRoomName = (room) => {
    if (room.type === 'group') return room.name;
    const other = room.members?.find(m => m._id !== user._id && m._id !== user.id);
    return other?.name || 'Unknown';
  };

  const getRoomInitial = (room) => {
    if (room.type === 'group') return room.name?.charAt(0).toUpperCase() || 'G';
    const other = room.members?.find(m => m._id !== user._id && m._id !== user.id);
    return other?.name?.charAt(0).toUpperCase() || '?';
  };

  const filteredRooms = rooms.filter(r =>
    getRoomName(r).toLowerCase().includes(roomSearch.toLowerCase())
  );

  const filteredUsers = allUsers.filter(u =>
    u.name.toLowerCase().includes(userSearch.toLowerCase())
  );

  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const totalUnread = rooms.reduce((sum, r) => sum + (r.unreadCount || 0), 0);

  return (
    <div className="chat-layout">
      {/* Sidebar */}
      <div className="chat-sidebar">
        <div className="chat-sidebar-header">
          <h2>Chat {totalUnread > 0 && <span className="chat-unread-badge">{totalUnread}</span>}</h2>
          <div className="chat-header-actions">
            <button className="chat-icon-btn" title="New Direct Message" onClick={() => { setShowUserList(v => !v); setShowGroupModal(false); }}>
              <FiUser />
            </button>
            <button className="chat-icon-btn" title="New Group" onClick={() => { setShowGroupModal(v => !v); setShowUserList(false); }}>
              <FiUsers />
            </button>
          </div>
        </div>

        <div className="chat-search-box">
          <FiSearch />
          <input placeholder="Search conversations..." value={roomSearch} onChange={e => setRoomSearch(e.target.value)} />
        </div>

        {/* New Direct Chat panel */}
        {showUserList && (
          <div className="chat-user-picker">
            <div className="chat-user-picker-header">
              <span>Start Direct Chat</span>
              <button onClick={() => setShowUserList(false)}><FiX /></button>
            </div>
            <div className="chat-search-box" style={{ margin: '0 0 0.5rem' }}>
              <FiSearch />
              <input placeholder="Search users..." value={userSearch} onChange={e => setUserSearch(e.target.value)} autoFocus />
            </div>
            <div className="chat-user-list">
              {filteredUsers.map(u => (
                <div key={u._id} className="chat-user-item" onClick={() => startDirectChat(u)}>
                  <div className="chat-avatar chat-avatar-sm">{u.name.charAt(0).toUpperCase()}</div>
                  <div>
                    <div className="chat-user-name">{u.name}</div>
                    <div className="chat-user-role">{u.role}</div>
                  </div>
                </div>
              ))}
              {filteredUsers.length === 0 && <p className="chat-empty-text">No users found</p>}
            </div>
          </div>
        )}

        {/* Room list */}
        <div className="chat-room-list">
          {filteredRooms.map(room => (
            <div
              key={room._id}
              className={`chat-room-item ${activeRoom?._id === room._id ? 'active' : ''}`}
              onClick={() => openRoom(room)}
            >
              <div className={`chat-avatar ${room.type === 'group' ? 'chat-avatar-group' : ''}`}>
                {getRoomInitial(room)}
              </div>
              <div className="chat-room-info">
                <div className="chat-room-name">
                  {room.type === 'group' && <FiUsers style={{ marginRight: 4, fontSize: '0.75rem' }} />}
                  {getRoomName(room)}
                </div>
                <div className="chat-room-last">
                  {room.lastMessage
                    ? `${room.lastMessage.sender?.name?.split(' ')[0]}: ${room.lastMessage.content || (room.lastMessage.fileName ? `📎 ${room.lastMessage.fileName}` : '')}`
                    : 'No messages yet'}
                </div>
              </div>
              <div className="chat-room-meta">
                <div className="chat-room-time">{formatTime(room.lastActivity)}</div>
                {room.unreadCount > 0 && (
                  <span className="chat-unread-badge">{room.unreadCount}</span>
                )}
              </div>
            </div>
          ))}
          {filteredRooms.length === 0 && (
            <div className="chat-empty-state">
              <FiMessageCircle size={32} />
              <p>No conversations yet</p>
              <p style={{ fontSize: '0.8rem', color: '#9ca3af' }}>Click <FiUser style={{ verticalAlign: 'middle' }} /> to start chatting</p>
            </div>
          )}
        </div>
      </div>

      {/* Message area */}
      <div className="chat-main">
        {activeRoom ? (
          <>
            <div className="chat-main-header">
              <div className="chat-avatar" style={{ flexShrink: 0 }}>{getRoomInitial(activeRoom)}</div>
              <div>
                <div className="chat-main-name">
                  {activeRoom.type === 'group' && <FiUsers style={{ marginRight: 6 }} />}
                  {getRoomName(activeRoom)}
                </div>
                {activeRoom.type === 'group' && (
                  <div className="chat-main-sub">{activeRoom.members?.length} members</div>
                )}
                {typingUsers.length > 0 && (
                  <div className="chat-typing">{typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...</div>
                )}
              </div>
            </div>

            <div className="chat-messages">
              {messages.map((msg, i) => {
                const isMine = msg.sender?._id === user._id || msg.sender?._id === user.id;
                const showSenderName = activeRoom.type === 'group' && !isMine &&
                  (i === 0 || messages[i - 1]?.sender?._id !== msg.sender?._id);
                return (
                  <div key={msg._id || i} className={`chat-msg-row ${isMine ? 'mine' : 'theirs'}`}>
                    {!isMine && (
                      <div className="chat-avatar chat-avatar-sm chat-msg-avatar">
                        {msg.sender?.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="chat-msg-bubble-wrap">
                      {showSenderName && <div className="chat-sender-name">{msg.sender?.name}</div>}
                      <div className={`chat-msg-bubble ${isMine ? 'bubble-mine' : 'bubble-theirs'}`}>
                        {msg.fileUrl ? renderFileMessage(msg) : msg.content}
                      </div>
                      <div className="chat-msg-time">{formatTime(msg.createdAt)}</div>
                    </div>
                  </div>
                );
              })}
              {messages.length === 0 && (
                <div className="chat-empty-state">
                  <FiMessageCircle size={40} />
                  <p>No messages yet. Say hello!</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <form className="chat-input-row" onSubmit={sendMessage}>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={sendFile}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
              />
              <button type="button" className="chat-icon-btn" title="Attach file"
                onClick={() => fileInputRef.current?.click()}>
                <FiPaperclip />
              </button>
              <input
                className="chat-input"
                placeholder="Type a message..."
                value={input}
                onChange={handleTyping}
                autoFocus
              />
              <button type="submit" className="chat-send-btn" disabled={!input.trim()}>
                <FiSend />
              </button>
            </form>
          </>
        ) : (
          <div className="chat-empty-state" style={{ flex: 1 }}>
            <FiMessageCircle size={56} style={{ color: '#c7d2fe' }} />
            <h3 style={{ color: '#6b7280', marginTop: '1rem' }}>Select a conversation</h3>
            <p style={{ color: '#9ca3af' }}>or start a new one with the buttons above</p>
          </div>
        )}
      </div>

      {/* Create Group Modal */}
      {showGroupModal && (
        <div className="modal-overlay" onClick={() => setShowGroupModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h3>Create Group</h3>
              <button className="modal-close" onClick={() => setShowGroupModal(false)}><FiX /></button>
            </div>
            <div className="form-group">
              <label>Group Name *</label>
              <input value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="e.g. Design Team" autoFocus />
            </div>
            <div className="form-group">
              <label>Add Members</label>
              <div className="chat-search-box" style={{ marginBottom: '0.5rem' }}>
                <FiSearch />
                <input placeholder="Search users..." value={userSearch} onChange={e => setUserSearch(e.target.value)} />
              </div>
              <div className="chat-user-list" style={{ maxHeight: 200 }}>
                {filteredUsers.map(u => {
                  const checked = groupMembers.includes(u._id);
                  return (
                    <div key={u._id} className="chat-user-item" onClick={() => setGroupMembers(prev =>
                      checked ? prev.filter(id => id !== u._id) : [...prev, u._id]
                    )}>
                      <input type="checkbox" checked={checked} readOnly style={{ marginRight: 8 }} />
                      <div className="chat-avatar chat-avatar-sm">{u.name.charAt(0).toUpperCase()}</div>
                      <div>
                        <div className="chat-user-name">{u.name}</div>
                        <div className="chat-user-role">{u.role}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {groupMembers.length > 0 && (
                <p style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.4rem' }}>
                  {groupMembers.length} member{groupMembers.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button className="btn-secondary" onClick={() => setShowGroupModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={createGroup}><FiPlus /> Create Group</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatPage;
